import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { ExportDropdown } from "@components/common/export/export-dropdown";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useExportAllPaginated } from "@hooks/use-export-all-paginated";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { downloadMembersData } from "@utils/file-data-export";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { useCallback, useEffect, useRef } from "react";
/** Dropdown to export members in the file format selected by the user */
export function BulkExportMembers(t0) {
  const $ = _c(28);
  const {
    subjectType,
    entityName,
    selectedEntities,
    areEntitiesSelected,
    teamId,
    companyId,
    buttonType: t1
  } = t0;
  const buttonType = t1 === undefined ? "bulk" : t1;
  const tableType = useAppSelector(tableTypeSelector);
  const {
    canAssignManagers,
    canAssignProjectRoles
  } = useHasUserValidFeatureCompanyLevel();
  const cachedMembersRef = useRef(null);
  const shouldExcludeRole = subjectType === "group" && !canAssignManagers || subjectType === "project" && !canAssignProjectRoles;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      cachedMembersRef.current = null;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== selectedEntities) {
    t3 = [selectedEntities];
    $[1] = selectedEntities;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  const {
    exportAll
  } = useExportAllPaginated();
  let t4;
  if ($[3] !== companyId || $[4] !== exportAll || $[5] !== teamId) {
    t4 = async options => {
      if (cachedMembersRef.current) {
        return cachedMembersRef.current;
      }
      const result = await exportAll({
        fetchParams: {
          companyId,
          teamId
        },
        fetchPage: _temp,
        transform: _temp3,
        onProgress: options?.onProgress,
        signal: options?.signal
      });
      if (result === null) {
        return null;
      }
      cachedMembersRef.current = result;
      return result;
    };
    $[3] = companyId;
    $[4] = exportAll;
    $[5] = teamId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const fetchAllTeamMembers = t4;
  let t5;
  if ($[7] !== areEntitiesSelected || $[8] !== companyId || $[9] !== fetchAllTeamMembers || $[10] !== selectedEntities || $[11] !== subjectType || $[12] !== teamId) {
    t5 = async options_0 => {
      if (areEntitiesSelected) {
        return selectedEntities;
      }
      if (subjectType === "team" && teamId && companyId) {
        return await fetchAllTeamMembers(options_0);
      }
      return selectedEntities;
    };
    $[7] = areEntitiesSelected;
    $[8] = companyId;
    $[9] = fetchAllTeamMembers;
    $[10] = selectedEntities;
    $[11] = subjectType;
    $[12] = teamId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const getMembersToExport = t5;
  let t6;
  if ($[14] !== entityName || $[15] !== getMembersToExport || $[16] !== shouldExcludeRole || $[17] !== subjectType) {
    t6 = async (format, options_1) => {
      const fileName = generateFileName(subjectType, entityName);
      const membersToExport = await getMembersToExport(options_1);
      if (membersToExport === null) {
        return null;
      }
      return await downloadMembersData({
        fileName,
        members: membersToExport,
        subjectType,
        format,
        shouldExcludeRole
      });
    };
    $[14] = entityName;
    $[15] = getMembersToExport;
    $[16] = shouldExcludeRole;
    $[17] = subjectType;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleExport = t6;
  const t7 = tableType ?? undefined;
  let t8;
  if ($[19] !== areEntitiesSelected || $[20] !== selectedEntities.length || $[21] !== t7) {
    t8 = {
      dataType: t7,
      numberOfEntities: selectedEntities.length,
      areEntitiesSelected
    };
    $[19] = areEntitiesSelected;
    $[20] = selectedEntities.length;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      success: "Successfully exported members.",
      error: "Cannot export members. Please try again."
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== buttonType || $[25] !== handleExport || $[26] !== t8) {
    t10 = <ExportDropdown trackingProps={t8} exportHandler={handleExport} toastMessage={t9} buttonTooltipText="Export members" buttonType={buttonType} />;
    $[24] = buttonType;
    $[25] = handleExport;
    $[26] = t8;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}

/** Returns the file name */
function _temp3(teamMembers) {
  return teamMembers.map(_temp2);
}
function _temp2(member) {
  return {
    ...member.userResponse,
    role: "MEMBER"
  };
}
async function _temp(params) {
  const response = await faroCoreApiClient.teams.fetchTeamMembers(params);
  return {
    data: response.data,
    next: response.page.next
  };
}
function generateFileName(subjectType, entityName) {
  switch (subjectType) {
    case "workspace":
      return `Company ${entityName} - Member List`;
    case "project":
      return `Project ${entityName} - Member List`;
    case "snapshot":
      return `Snapshot ${entityName} - Member List`;
    case "group":
      return `Group ${entityName} - Member List`;
    case "team":
    case "workspaceTeam":
      return `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} ${entityName} - Member List`;
    case "activeMembers":
      return `Company ${entityName} - Active Members List`;
  }
}